<template>
  <div class="google-callback">
    <VContainer class="fill-height">
      <VLayout row wrap align-center justify-center>
        <VFlex xs12 sm8 md6>
          <VCard class="elevation-12">
            <VCardText class="text-center pa-8">
              <div v-if="isProcessing">
                <VProgressCircular
                  indeterminate
                  color="primary"
                  size="50"
                  class="my-4"
                ></VProgressCircular>
                <h3>Processing Authentication...</h3>
                <p class="mt-2">Please wait while we complete your sign-in.</p>
              </div>

              <div v-else-if="error">
                <VIcon color="error" size="50" class="mb-4"
                  >mdi-alert-circle</VIcon
                >
                <h3 class="error--text">Sign In Failed</h3>
                <p class="mt-2"
                  >We couldn't complete your sign in. Please try again or
                  contact support if the problem persists.</p
                >
                <div class="mt-2 text-xs-center">
                  <small class="text--darken-1">
                    <code class="grey--text">{{ error }}</code>
                  </small>
                </div>
                <VBtn class="mt-4" color="primary" @click="goHome">
                  Try Again
                </VBtn>
              </div>

              <div v-else-if="success">
                <VIcon color="success" size="50" class="mb-4"
                  >mdi-check-circle</VIcon
                >
                <h3 class="success--text">Welcome!</h3>
                <p class="mt-2"
                  >Hi {{ $store.getters.displayName }}, you're all set.</p
                >
                <p class="mt-3">Redirecting you to the dashboard...</p>
              </div>
            </VCardText>
          </VCard>
        </VFlex>
      </VLayout>
    </VContainer>
  </div>
</template>

<script>
import { VContainer, VLayout, VFlex } from 'vuetify/lib'
import { VCard, VCardText } from '@vuetify/VCard'
import VProgressCircular from '@vuetify/VProgressCircular'
import VIcon from '@vuetify/VIcon'
import VBtn from '@vuetify/VBtn/'

export default {
  name: 'GoogleCallback',
  components: {
    VContainer,
    VLayout,
    VFlex,
    VCard,
    VCardText,
    VProgressCircular,
    VIcon,
    VBtn,
  },
  data() {
    return {
      isProcessing: true,
      success: false,
      error: null,
      hasProcessed: false, // Prevent double processing
    }
  },
  computed: {},
  async mounted() {
    if (!this.hasProcessed) {
      this.hasProcessed = true
      await this.handleCallback()
    }
  },
  methods: {
    async handleCallback() {
      try {
        // Parse URL parameters
        const urlParams = new URLSearchParams(window.location.search)
        const code = urlParams.get('code')
        const state = urlParams.get('state')
        const error = urlParams.get('error')

        // Immediately clean URL to prevent reuse
        if (window.location.search) {
          try {
            const url = new URL(window.location)
            url.search = ''
            window.history.replaceState({}, document.title, url.pathname)
          } catch (e) {
            console.error('Failed to clean URL after OAuth callback:', e)
            // Continue processing - URL cleaning is not critical for auth flow
          }
        }

        // Handle OAuth error response
        if (error) {
          const errorDescription =
            urlParams.get('error_description') || 'Unknown error'
          throw new Error(`OAuth error: ${error} - ${errorDescription}`)
        }

        // Validate required parameters
        if (!code) {
          throw new Error('Missing authorization code')
        }

        if (!state) {
          throw new Error('Missing state parameter')
        }

        // Verify state parameter for CSRF protection
        const storedState = sessionStorage.getItem('oauth_state')
        if (state !== storedState) {
          throw new Error('Invalid state parameter - possible CSRF attack')
        }

        // Get PKCE code verifier
        const codeVerifier = sessionStorage.getItem('oauth_code_verifier')
        if (!codeVerifier) {
          throw new Error(
            'Missing PKCE code verifier - authentication flow corrupted'
          )
        }

        // Exchange authorization code for tokens using Vuex store
        const authResult = await this.$store.dispatch(
          'verifyGoogleAuth',
          {
            code: code,
            codeVerifier: codeVerifier,
            state: state,
          }
        )

        if (authResult.success) {
          // Successfully authenticated and stored in Vuex store
          this.success = true
          this.isProcessing = false

          // Clean up session storage
          sessionStorage.removeItem('oauth_code_verifier')
          sessionStorage.removeItem('oauth_state')

          // Clean up URL
          window.history.replaceState({}, document.title, this.$route.path)

          // Redirect to dashboard after a short delay
          setTimeout(() => {
            this.$router.push('/dashboard')
          }, 1500)
        } else {
          // Authentication failed, error should be in Vuex store
          throw new Error(authResult.error || 'Authentication failed')
        }
      } catch (error) {
        console.error('OAuth callback error:', error)
        this.error = error.message
        this.isProcessing = false

        // Clean up session storage on error
        sessionStorage.removeItem('oauth_code_verifier')
        sessionStorage.removeItem('oauth_state')
      }
    },

    goHome() {
      this.$router.push('/')
    },
  },
}
</script>

<style scoped>
.google-callback {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}
</style>
